import { formatIqd, fromMillidinars } from '@gc/shared';

/**
 * What the audit log screen needs to SAY about a row, apart from how it is
 * drawn — so the label set and the filters are pinned in plain node.
 *
 * Action names are the writers' own strings (`bill.delete`, `tab.serviceChargeWaived`).
 * They are turned into translation keys by swapping the dots for underscores,
 * because the names nest: `employee.changeOwnPassword` and
 * `employee.changeOwnPassword.refused` cannot both be leaves of one JSON tree.
 */

/**
 * Every action a server writer produces today, plus the two refund actions
 * requested from the customers module (listed so their label is ready the day
 * they land). An action missing here still renders — as its raw name — so a
 * new writer can never make a row disappear, only look unfinished.
 */
export const KNOWN_AUDIT_ACTIONS = [
  'accountVault.setPassword',
  'accountVault.unlocked',
  'accountVault.unlockFailed',
  'app.update_pushed',
  'auth.login.locked',
  'bill.delete',
  'bill.edit',
  'cashDrawer.autoOpened',
  'cashDrawer.closed',
  'cashDrawer.movement',
  'cashDrawer.started',
  'customer.balanceRefund',
  'device.freeMode',
  'device.launcherNotice.done',
  'device.power',
  'device.setGame',
  'electronicsProvider.create',
  'electronicsProvider.delete',
  'electronicsProvider.update',
  'employee.changeOwnPassword',
  'employee.changeOwnPassword.refused',
  'employee.create',
  'employee.deactivate',
  'employee.delete',
  'employee.resetPassword',
  'employee.setDiscountLimit',
  'employee.updatePermissions',
  'expense.create',
  'expense.delete',
  'expense.update',
  'gameAccount.bulkAssignHomeDevices',
  'gameAccount.bulkLinkGame',
  'gameAccount.create',
  'gameAccount.delete',
  'gameAccount.revealEmailPassword',
  'gameAccount.revealPassword',
  'gameAccount.update',
  'hotkey.unlock',
  'income.create',
  'income.delete',
  'income.update',
  'order.discount',
  'order.refund',
  'order.return',
  'permissionSet.apply',
  'permissionSet.create',
  'permissionSet.delete',
  'permissionSet.update',
  'platform.ownerPasswordReset',
  'platform.paidUntilChanged',
  'platform.sittingsEnded',
  'platform.venueCodeChanged',
  'platform.venueReactivated',
  'platform.venueSuspended',
  'portal.ownAccount',
  'portal.reload_pushed',
  'product.adjustStock',
  'product.delete',
  'product.hide',
  'product.update',
  'productCategory.update',
  'safe.bond_adjust',
  'safe.bond_delete',
  'safe.record',
  'session.free_play_relock',
  'session.free_play_screen',
  'session.pause_screen',
  'session.powered_off',
  'session.resume_screen',
  'session.void',
  'settings.update',
  'settings.updatePublicProfile',
  'shift.close',
  'shift.forceClose',
  'shift.open',
  'tab.changeVisitor',
  'tab.closeEmpty',
  'tab.moveItems',
  'tab.serviceChargeNotQuoted',
  'tab.serviceChargeWaived',
  'tab.splitOrderBill',
  'tag.update',
] as const;

/** `bill.delete` -> `auditLog.actions.bill_delete`. */
export function auditActionKey(action: string): string {
  return `auditLog.actions.${action.replaceAll('.', '_')}`;
}

/**
 * The Area filter: one action PREFIX each, because that is what the API
 * filters on. `product` without its dot on purpose, so stock and categories
 * come with the products.
 */
export const AUDIT_AREAS = [
  'bill.',
  'tab.',
  'session.',
  'order.',
  'customer.',
  'safe.',
  'shift.',
  'cashDrawer.',
  'expense.',
  'income.',
  'employee.',
  'permissionSet.',
  'settings.',
  'product',
  'gameAccount.',
  'accountVault.',
  'hotkey.',
  'device.',
  'electronicsProvider.',
  'auth.',
  'platform.',
] as const;

/**
 * Whether an action filter is one of the Area prefixes — anything else is an
 * exact action picked off a row, and must be matched exactly: action names
 * nest, so "employee.changeOwnPassword" as a prefix would also bring every
 * "employee.changeOwnPassword.refused" (review find, 2026-09-24).
 */
export function isAuditAreaPrefix(action: string): boolean {
  return (AUDIT_AREAS as readonly string[]).includes(action);
}

/** `bill.` -> `auditLog.areas.bill`. */
export function auditAreaKey(prefix: string): string {
  return `auditLog.areas.${prefix.replace(/\.$/, '')}`;
}

/** The entity types the writers use — the Entity filter's choices. */
export const AUDIT_ENTITY_TYPES = [
  'Bill',
  'Tab',
  'Session',
  'Order',
  'CustomerAccount',
  'BoxReset',
  'SafeTransaction',
  'Shift',
  'Expense',
  'Income',
  'Employee',
  'PermissionSet',
  'VenueSettings',
  'Product',
  'ProductCategory',
  'Tag',
  'GameAccount',
  'Game',
  'Device',
  'ElectronicsProvider',
  'Venue',
  // Done on a board launcher notice answers an audit row (2026-10-05).
  'AuditLog',
] as const;

/* -------------------------------------------------------------------------- */
/* Filters -> query string                                                    */
/* -------------------------------------------------------------------------- */

export interface AuditFilters {
  /** `datetime-local` values, venue-local as the browser reads them; '' = open. */
  from: string;
  to: string;
  employeeId: string;
  /** A prefix from {@link AUDIT_AREAS}, or an exact action picked off a row. */
  action: string;
  entityType: string;
  entityId: string;
  q: string;
  /** The automatic screen pushes, hidden unless asked for (`routine=hide`). */
  showRoutine: boolean;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  from: '',
  to: '',
  employeeId: '',
  action: '',
  entityType: '',
  entityId: '',
  q: '',
  showRoutine: false,
};

export const AUDIT_PAGE_SIZE = 50;

/** A `datetime-local` value as the ISO instant the API bounds on, or null. */
function toInstant(local: string): string | null {
  if (!local) return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** `GET /audit-log?…` for these filters, one page from `cursor`. */
export function auditLogPath(filters: AuditFilters, cursor: string | null = null): string {
  const params = new URLSearchParams({ limit: String(AUDIT_PAGE_SIZE) });
  const from = toInstant(filters.from);
  const to = toInstant(filters.to);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (filters.employeeId) params.set('employeeId', filters.employeeId);
  if (filters.action) {
    params.set('action', filters.action);
    if (!isAuditAreaPrefix(filters.action)) params.set('actionExact', '1');
  }
  if (filters.entityType) params.set('entityType', filters.entityType);
  if (filters.entityId) params.set('entityId', filters.entityId);
  const q = filters.q.trim();
  if (q) params.set('q', q.slice(0, 120));
  if (!filters.showRoutine) params.set('routine', 'hide');
  if (cursor) params.set('cursor', cursor);
  return `/audit-log?${params.toString()}`;
}

/* -------------------------------------------------------------------------- */
/* Before / after                                                             */
/* -------------------------------------------------------------------------- */

export interface AuditChangeRow {
  key: string;
  before: unknown;
  after: unknown;
  changed: boolean;
  /** Present on one side only. */
  onlyOn: 'before' | 'after' | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Field-by-field rows for a before/after pair — or null when either side is
 * not a plain object, and the raw JSON is the honest view.
 *
 * `settings.update` stores the WHOLE settings row on both sides, forty-odd
 * fields of which one changed; unchanged fields are left out unless asked
 * for, or the one that matters is lost in the list.
 */
export function auditChangeRows(
  before: unknown,
  after: unknown,
  showUnchanged: boolean,
): AuditChangeRow[] | null {
  const left = before ?? {};
  const right = after ?? {};
  if (!isRecord(left) || !isRecord(right)) return null;

  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])];
  return keys
    .map((key) => {
      const inBefore = key in left;
      const inAfter = key in right;
      return {
        key,
        before: left[key],
        after: right[key],
        // A field on one side only is shown as it stands: an "after" with no
        // "before" at all (a create, a reveal) is a fact, not a change.
        changed: inBefore && inAfter && !sameValue(left[key], right[key]),
        onlyOn: inBefore && inAfter ? null : inBefore ? ('before' as const) : ('after' as const),
      };
    })
    .filter((row) => showUnchanged || row.changed || row.onlyOn !== null);
}

/** How many fields a pair has that did NOT change — for the "show all" toggle. */
export function unchangedCount(before: unknown, after: unknown): number {
  const rows = auditChangeRows(before, after, true);
  return rows ? rows.filter((row) => !row.changed && row.onlyOn === null).length : 0;
}

/**
 * One stored value as text. Money fields (the `…Md` millidinar strings every
 * writer uses) read as IQD, because "5000000" is not a number anyone at this
 * venue thinks in.
 */
export function formatAuditValue(
  key: string,
  value: unknown,
  options: { currencySymbol: string; yes: string; no: string; empty: string },
): string {
  if (value === null || value === undefined || value === '') return options.empty;
  if (typeof value === 'boolean') return value ? options.yes : options.no;
  if (/Md$/.test(key) && (typeof value === 'string' || typeof value === 'number')) {
    const text = String(value);
    if (/^-?\d+$/.test(text)) {
      return formatIqd(fromMillidinars(text), { currencySymbol: options.currencySymbol });
    }
  }
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return JSON.stringify(value);
}
